import React, {useMemo, useState} from 'react';
import {Head, Link} from 'zudoku/components';
import {Braces, Layers, Radio, Search, TrendingUp, Users} from 'lucide-react';
import {getProducts, type Locale} from '../data/products';
import styles from './catalog.module.css';

type Filter = 'all' | 'core' | 'advanced' | 'institutional';

function ProductCard({product, zh}: {product: ReturnType<typeof getProducts>[number]; zh: boolean}) {
  return (
    <article className={styles.card}>
      <div className={styles.cardTop}>
        <Link to={product.href}>{product.name}</Link>
        <span>{product.count} {zh ? '个接口' : 'endpoints'}</span>
      </div>
      <p>{product.description}</p>
      <div className={styles.protocols}>
        {product.protocols.map((protocol) => (
          <Link className={protocol.kind === 'REST' ? styles.rest : styles.ws} key={protocol.kind} to={protocol.href}>
            {protocol.kind === 'REST' ? <Braces size={15} /> : <Radio size={15} />}
            {protocol.label}
          </Link>
        ))}
      </div>
    </article>
  );
}

export default function Catalog({locale}: {locale: Locale}): React.ReactNode {
  const zh = locale === 'zh-CN';
  const products = useMemo(() => getProducts(locale), [locale]);
  const filters: Array<{id: Filter; label: string}> = [
    {id: 'all', label: zh ? '全部' : 'All'},
    {id: 'core', label: zh ? '核心交易' : 'Core Trading'},
    {id: 'advanced', label: zh ? '高级交易' : 'Advanced Trading'},
    {id: 'institutional', label: zh ? 'VIP 与机构' : 'VIP & Institutional'},
  ];
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const visible = useMemo(() => products.filter((product) => {
    const matchesCategory = filter === 'all' || product.category === filter;
    const searchText = `${product.name} ${product.description}`.toLowerCase();
    return matchesCategory && searchText.includes(query.trim().toLowerCase());
  }), [filter, query, products]);

  const groups = [
    {id: 'core' as const, title: zh ? '核心交易' : 'Core Trading', icon: TrendingUp},
    {id: 'advanced' as const, title: zh ? '高级交易' : 'Advanced Trading', icon: Layers},
    {id: 'institutional' as const, title: zh ? 'VIP 与机构' : 'VIP & Institutional', icon: Users},
  ];

  return (
    <>
      <Head>
        <title>{zh ? 'API 参考' : 'API Reference'} | WEEX</title>
        <meta name="description" content={zh ? '浏览 WEEX V3 API 产品与接口' : 'Browse WEEX V3 API products and endpoints'} />
      </Head>
      <main className={styles.page}>
        <h1>{zh ? 'API 参考' : 'API Reference'}</h1>
        <p className={styles.lead}>{zh
          ? `探索 WEEX 核心交易、高级交易、VIP 与机构业务的 REST API 和 WebSocket 数据流，包含 ${products.length} 个产品、${products.reduce((sum, item) => sum + item.count, 0)} 个接口。`
          : `Explore REST APIs and WebSocket Streams for WEEX Core Trading, Advanced Trading, and VIP & Institutional products, covering ${products.length} products and ${products.reduce((sum, item) => sum + item.count, 0)} endpoints.`}
        </p>
        <div className={styles.tools}>
          <label className={styles.searchBox}>
            <Search size={18} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={zh ? '搜索产品...' : 'Search products...'} />
            <kbd>/</kbd>
          </label>
          <div className={styles.filters} role="group" aria-label={zh ? '产品分类' : 'Product categories'}>
            {filters.map((item) => (
              <button className={filter === item.id ? styles.selected : ''} key={item.id} type="button" onClick={() => setFilter(item.id)}>
                {item.label}
              </button>
            ))}
          </div>
        </div>
        {groups.map((group) => {
          const entries = visible.filter((product) => product.category === group.id);
          if (!entries.length) return null;
          const Icon = group.icon;
          return (
            <section className={styles.group} key={group.id}>
              <h2><Icon size={20} />{group.title}</h2>
              <div className={styles.grid}>{entries.map((product) => <ProductCard key={product.id} product={product} zh={zh} />)}</div>
            </section>
          );
        })}
        {!visible.length && <div className={styles.empty}>{zh ? '没有找到匹配的 API 产品。' : 'No matching API products found.'}</div>}
      </main>
    </>
  );
}
